import { useState, useEffect } from 'react';
import axios from 'axios';
import { Filter, Download, TrendingUp, TrendingDown, ArrowRightLeft, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';
import { isAdmin } from '@/utils/permissions';
import { getSessionItem } from '@/utils/sessionManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TransactionManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [movementTypes, setMovementTypes] = useState({});
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  const [filters, setFilters] = useState({
    item_id: '',
    warehouse_id: '',
    movement_type: '',
    start_date: '',
    end_date: ''
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('txn_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTxn, setNewTxn] = useState({
    movement_type: '',
    item_id: '',
    warehouse_id: '',
    qty: 0,
    reference: '',
    remarks: '',
    txn_date: new Date().toISOString().split('T')[0],
    to_warehouse_id: '',
    vendor_id: '',
    customer_id: '',
    cost_center: ''
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, sortBy, sortOrder, filters]);

  const fetchMasterData = async () => {
    try {
      const [typeRes, itemRes, whRes, vendorRes, customerRes] = await Promise.all([
        axios.get(`${API}/transactions/movement-types`),
        axios.get(`${API}/items`),
        axios.get(`${API}/warehouses`),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/customers`)
      ]);
      
      setMovementTypes(typeRes.data);
      setItems(itemRes.data);
      setWarehouses(whRes.data);
      setVendors(vendorRes.data);
      setCustomers(customerRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      // Add filters
      if (filters.item_id) params.append('item_id', filters.item_id);
      if (filters.warehouse_id) params.append('warehouse_id', filters.warehouse_id);
      if (filters.movement_type) params.append('movement_type', filters.movement_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await axios.get(`${API}/transactions/manual?${params.toString()}`);
      
      if (response.data.data) {
        setTransactions(response.data.data);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setTransactions(response.data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transaction data');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilter = async () => {
    try {
      const params = {};
      if (filters.item_id) params.item_id = filters.item_id;
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      if (filters.movement_type) params.movement_type = filters.movement_type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const res = await axios.get(`${API}/transactions/manual`, { params });
      // Handle paginated response format
      const txnData = res.data.data || res.data;
      setTransactions(Array.isArray(txnData) ? txnData : []);
    } catch (error) {
      toast.error('Failed to apply filters');
    }
  };

  const handleCreateTransaction = async () => {
    if (!newTxn.movement_type || !newTxn.item_id || !newTxn.warehouse_id || newTxn.qty <= 0) {
      toast.error('Please fill all required fields (Movement Type, Item, Warehouse, Qty > 0)');
      return;
    }
    const config = movementTypes[newTxn.movement_type];
    if (config) {
      for (const req of (config.requires || [])) {
        if (!newTxn[req]) {
          toast.error(`Movement type ${newTxn.movement_type} requires ${req.replace('_', ' ')}`);
          return;
        }
      }
    }
    try {
      const sessionToken = getSessionItem('sessionToken');
      await axios.post(`${API}/transactions/manual`, newTxn, {
        headers: { 'x-session-token': sessionToken || '' }
      });
      toast.success('Manual transaction created successfully');
      setShowCreateDialog(false);
      setNewTxn({ movement_type: '', item_id: '', warehouse_id: '', qty: 0, reference: '', remarks: '', txn_date: new Date().toISOString().split('T')[0], to_warehouse_id: '', vendor_id: '', customer_id: '', cost_center: '' });
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transaction');
    }
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getWarehouseName = (id) => {
    const wh = warehouses.find(w => w.id === id);
    return wh ? wh.name : id;
  };

  const getDirectionIcon = (direction) => {
    if (direction === 'receipt') return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (direction === 'issue') return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <ArrowRightLeft className="w-4 h-4 text-blue-600" />;
  };

  const getDirectionBadge = (direction) => {
    const colors = {
      'receipt': 'bg-green-100 text-green-800',
      'issue': 'bg-red-100 text-red-800',
      'transfer': 'bg-blue-100 text-blue-800'
    };
    return colors[direction] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Transaction Management</h2>
          <p className="text-slate-600">Transactions are automatically created from system operations (GRN, Production, Shipments)</p>
        </div>
        {isAdmin() && (
          <Button data-testid="add-manual-txn-btn" onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Manual Transaction
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Total Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{transactions.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Receipts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <div className="text-2xl font-bold text-green-600">
                {transactions.filter(t => t.direction === 'receipt').length}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Consumption</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <div className="text-2xl font-bold text-red-600">
                {transactions.filter(t => t.direction === 'issue').length}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">SKUs Affected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(transactions.map(t => t.item_id)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            <CardTitle>Filters</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Item</label>
              <SearchableItemSelect
                items={items}
                value={filters.item_id || 'all'}
                onValueChange={(value) => setFilters({ ...filters, item_id: value === 'all' ? '' : value })}
                placeholder="All Items"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Warehouse</label>
              <select
                className="w-full border border-slate-300 rounded-md p-2"
                value={filters.warehouse_id}
                onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
              >
                <option value="">All Warehouses</option>
                {warehouses.map(wh => (
                  <option key={wh.id} value={wh.id}>{wh.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Movement Type</label>
              <select
                className="w-full border border-slate-300 rounded-md p-2"
                value={filters.movement_type}
                onChange={(e) => setFilters({ ...filters, movement_type: e.target.value })}
              >
                <option value="">All Types</option>
                {Object.entries(movementTypes).map(([code, config]) => (
                  <option key={code} value={code}>
                    {code} - {config.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">From Date</label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">To Date</label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button onClick={handleFilter}>Apply Filters</Button>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ item_id: '', warehouse_id: '', movement_type: '', start_date: '', end_date: '' });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
          <CardDescription>Complete audit trail of all inventory movements</CardDescription>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>No transactions found. Create your first transaction above.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Movement Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Direction</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Item</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Warehouse</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Quantity</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Reference</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transactions.map((txn) => (
                    <tr key={txn.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm">{txn.txn_date}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{txn.movement_type}</Badge>
                          <span className="text-sm">{txn.movement_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getDirectionIcon(txn.direction)}
                          <Badge className={getDirectionBadge(txn.direction)}>
                            {txn.direction}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{getItemInfo(txn.item_id)}</td>
                      <td className="px-4 py-3 text-sm">{getWarehouseName(txn.warehouse_id)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${txn.direction === 'receipt' ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.direction === 'receipt' ? '+' : '-'}{txn.qty}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{txn.reference || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{txn.remarks || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination Controls */}
              <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>

    {/* Create Manual Transaction Dialog - Admin Only */}
    <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Manual Transaction</DialogTitle>
          <DialogDescription>Create a manual inventory transaction (Admin only)</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Movement Type *</Label>
            <Select value={newTxn.movement_type} onValueChange={(val) => setNewTxn({ ...newTxn, movement_type: val })}>
              <SelectTrigger data-testid="txn-movement-type"><SelectValue placeholder="Select movement type" /></SelectTrigger>
              <SelectContent>
                {Object.entries(movementTypes).map(([code, config]) => (
                  <SelectItem key={code} value={code}>{code} - {config.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Item *</Label>
            <SearchableItemSelect
              items={items}
              value={newTxn.item_id}
              onValueChange={(val) => setNewTxn({ ...newTxn, item_id: val === 'all' ? '' : val })}
              placeholder="Select item"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Warehouse *</Label>
              <Select value={newTxn.warehouse_id} onValueChange={(val) => setNewTxn({ ...newTxn, warehouse_id: val })}>
                <SelectTrigger data-testid="txn-warehouse"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity *</Label>
              <Input data-testid="txn-qty" type="number" min={1} value={newTxn.qty} onChange={(e) => setNewTxn({ ...newTxn, qty: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div>
            <Label>Transaction Date *</Label>
            <Input data-testid="txn-date" type="date" value={newTxn.txn_date} onChange={(e) => setNewTxn({ ...newTxn, txn_date: e.target.value })} />
          </div>
          {/* Conditional fields based on movement type */}
          {movementTypes[newTxn.movement_type]?.requires?.includes('vendor_id') && (
            <div>
              <Label>Vendor *</Label>
              <Select value={newTxn.vendor_id} onValueChange={(val) => setNewTxn({ ...newTxn, vendor_id: val })}>
                <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          {movementTypes[newTxn.movement_type]?.requires?.includes('customer_id') && (
            <div>
              <Label>Customer *</Label>
              <Select value={newTxn.customer_id} onValueChange={(val) => setNewTxn({ ...newTxn, customer_id: val })}>
                <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          {movementTypes[newTxn.movement_type]?.requires?.includes('to_warehouse_id') && (
            <div>
              <Label>Destination Warehouse *</Label>
              <Select value={newTxn.to_warehouse_id} onValueChange={(val) => setNewTxn({ ...newTxn, to_warehouse_id: val })}>
                <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
                <SelectContent>
                  {warehouses.filter(w => w.id !== newTxn.warehouse_id).map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {movementTypes[newTxn.movement_type]?.requires?.includes('cost_center') && (
            <div>
              <Label>Cost Center *</Label>
              <Input value={newTxn.cost_center} onChange={(e) => setNewTxn({ ...newTxn, cost_center: e.target.value })} placeholder="Enter cost center" />
            </div>
          )}
          {movementTypes[newTxn.movement_type]?.requires?.includes('reference') && (
            <div>
              <Label>Reference *</Label>
              <Input value={newTxn.reference} onChange={(e) => setNewTxn({ ...newTxn, reference: e.target.value })} placeholder="Enter reference" />
            </div>
          )}
          <div>
            <Label>Remarks</Label>
            <Input data-testid="txn-remarks" value={newTxn.remarks} onChange={(e) => setNewTxn({ ...newTxn, remarks: e.target.value })} placeholder="Enter remarks" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button data-testid="txn-submit-btn" onClick={handleCreateTransaction}>Create Transaction</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default TransactionManagement;
